import { lerp } from './math.esm.js';
import { typedKeys } from './object.esm.js';

function _array_like_to_array(arr, len) {
    if (len == null || len > arr.length) len = arr.length;
    for(var i = 0, arr2 = new Array(len); i < len; i++)arr2[i] = arr[i];
    return arr2;
}
function _array_without_holes(arr) {
    if (Array.isArray(arr)) return _array_like_to_array(arr);
}
function _class_call_check(instance, Constructor) {
    if (!(instance instanceof Constructor)) {
        throw new TypeError("Cannot call a class as a function");
    }
}
function _defineProperties(target, props) {
    for(var i = 0; i < props.length; i++){
        var descriptor = props[i];
        descriptor.enumerable = descriptor.enumerable || false;
        descriptor.configurable = true;
        if ("value" in descriptor) descriptor.writable = true;
        Object.defineProperty(target, descriptor.key, descriptor);
    }
}
function _create_class(Constructor, protoProps, staticProps) {
    if (protoProps) _defineProperties(Constructor.prototype, protoProps);
    if (staticProps) _defineProperties(Constructor, staticProps);
    return Constructor;
}
function _define_property(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _iterable_to_array(iter) {
    if (typeof Symbol !== "undefined" && iter[Symbol.iterator] != null || iter["@@iterator"] != null) return Array.from(iter);
}
function _non_iterable_spread() {
    throw new TypeError("Invalid attempt to spread non-iterable instance.\\nIn order to be iterable, non-array objects must have a [Symbol.iterator]() method.");
}
function _to_consumable_array(arr) {
    return _array_without_holes(arr) || _iterable_to_array(arr) || _unsupported_iterable_to_array(arr) || _non_iterable_spread();
}
function _unsupported_iterable_to_array(o, minLen) {
    if (!o) return;
    if (typeof o === "string") return _array_like_to_array(o, minLen);
    var n = Object.prototype.toString.call(o).slice(8, -1);
    if (n === "Object" && o.constructor) n = o.constructor.name;
    if (n === "Map" || n === "Set") return Array.from(n);
    if (n === "Arguments" || /^(?:Ui|I)nt(?:8|16|32)(?:Clamped)?Array$/.test(n)) return _array_like_to_array(o, minLen);
}
var DEFAULT_NORMAL_DISTRIBUTION_FACTOR = 6;
function randomWithSeed(seed) {
    var x = Math.sin(seed) * 10000;
    return x - Math.floor(x);
}
function randomBoolean() {
    var rng = arguments.length > 0 && arguments[0] !== void 0 ? arguments[0] : Math;
    return rng.random() > 0.5;
}
function randomInteger(min, max) {
    var rng = arguments.length > 2 && arguments[2] !== void 0 ? arguments[2] : Math;
    return Math.floor(randomFloat(min, max + 1, rng));
}
function randomFloat(min, max) {
    var rng = arguments.length > 2 && arguments[2] !== void 0 ? arguments[2] : Math;
    return lerp(min, max, rng.random());
}
function randomString(length, set) {
    var rng = arguments.length > 2 && arguments[2] !== void 0 ? arguments[2] : Math;
    var string = "";
    for(var i = 0; i < length; i++){
        string += sample(set, rng);
    }
    return string;
}
function randomUuid() {
    var rng = arguments.length > 0 && arguments[0] !== void 0 ? arguments[0] : Math;
    return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replaceAll(/[xy]/g, function(c) {
        var r = rng.random() * 16 | 0, v = c === "x" ? r : r & 0x3 | 0x8;
        return v.toString(16);
    });
}
function randomNormalDistribution() {
    var factor = arguments.length > 0 && arguments[0] !== void 0 ? arguments[0] : DEFAULT_NORMAL_DISTRIBUTION_FACTOR, rng = arguments.length > 1 && arguments[1] !== void 0 ? arguments[1] : Math;
    var rand = 0;
    for(var i = 0; i < factor; i += 1){
        rand += rng.random();
    }
    return rand / factor;
}
function randomByWeights(weights) {
    var rng = arguments.length > 1 && arguments[1] !== void 0 ? arguments[1] : Math;
    var keys = typedKeys(weights);
    var sortedKeysByWeights = keys.sort(function(a, b) {
        return weights[a] - weights[b];
    }); // eslint-disable-line @typescript-eslint/no-non-null-assertion
    var sortedWeights = sortedKeysByWeights.map(function(val) {
        return weights[val];
    });
    var _accumulated_;
    var accumulatedWeights = sortedWeights.reduce(function(accumulated, currentValue, index) {
        return _to_consumable_array(accumulated).concat([
            currentValue + ((_accumulated_ = accumulated[index - 1]) !== null && _accumulated_ !== void 0 ? _accumulated_ : 0)
        ]);
    }, []);
    var totalWeight = accumulatedWeights[accumulatedWeights.length - 1]; // eslint-disable-line @typescript-eslint/no-non-null-assertion
    var randomInt = randomInteger(0, totalWeight, rng);
    var matchingRangeIndex = accumulatedWeights.findIndex(function(accumulatedWeight) {
        return randomInt < accumulatedWeight;
    });
    return sortedKeysByWeights[matchingRangeIndex]; // eslint-disable-line @typescript-eslint/no-non-null-assertion
}
function sample(array) {
    var rng = arguments.length > 1 && arguments[1] !== void 0 ? arguments[1] : Math;
    return array[Math.floor(rng.random() * array.length)]; // eslint-disable-line @typescript-eslint/no-non-null-assertion
}
function shuffle(array) {
    var rng = arguments.length > 1 && arguments[1] !== void 0 ? arguments[1] : Math;
    var counter = array.length;
    while(counter > 0){
        var index = Math.floor(rng.random() * counter);
        counter--;
        var temp = array[counter];
        array[counter] = array[index]; // eslint-disable-line @typescript-eslint/no-non-null-assertion
        array[index] = temp; // eslint-disable-line @typescript-eslint/no-non-null-assertion
    }
    return array;
}
var RNG = /*#__PURE__*/ function() {
    function RNG(seed) {
        _class_call_check(this, RNG);
        _define_property(this, "seed", void 0);
        _define_property(this, "_counter", void 0);
        this.seed = seed;
        this._counter = 0;
    }
    _create_class(RNG, [
        {
            key: "counter",
            get: function get() {
                return this._counter;
            }
        },
        {
            key: "reset",
            value: function reset() {
                this._counter = 0;
            }
        },
        {
            key: "random",
            value: function random() {
                return randomWithSeed(this.seed + this._counter++);
            }
        }
    ]);
    return RNG;
}();

export { DEFAULT_NORMAL_DISTRIBUTION_FACTOR, RNG, randomBoolean, randomByWeights, randomFloat, randomInteger, randomNormalDistribution, randomString, randomUuid, sample, shuffle };
